import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle, Loading } from '../root/components/common'
import { useLocation, withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from './actions'
import { useReportsFilters } from '../root/hooks/useReportsFilters'
import { ReportsFilters } from '../root/components/common/reports-filters'
import { ReportsTable } from '../root/components/common/reports-table/reports-table'
import { isEmpty } from 'lodash'
import { useCallback } from 'react'
import debounce from 'lodash/debounce'
import { OptionSwitcher } from '../root/components/common/option-switcher/option-switcher'
import { REPRESENTATION } from '../root/constants'
import { UserPermissions } from '../../services/permissions'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { getFileForExport } from '../reports/api/report-api'
import classNames from 'classnames'
import { downloadFile } from 'web/src/utils'
import { enqueueSnackbar } from '../root/actions'
import {
    urlToSearchState,
    useSyncUrlState
} from '../../utils/hooks/useSyncUrlState'
import {
    composeFileName,
    isFiltersTimeRangeExceededForExport
} from '../../utils'
import { ExportMenu } from '../root/components/common/export-menu'
const useStyles = makeStyles(theme => ({
    filtersWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: 8
    },
    switcher: {
        marginRight: 12
    },
    filters: {},
    wrapper: {
        height: 'calc(100% - 125px)'
    },
    nameLink: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer'
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    },
    icon: {
        color: theme.primary,
        cursor: 'pointer'
    },
    batteryWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    statusWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    modalButton: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 15
    },
    pickerWrapper: {
        marginTop: 15
    },
    restoreIconButton: {
        color: theme.primary
    },
    exportBtnsWrapper: {
        display: 'flex',
        gap: '6px',
        marginRight: '16px'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: '20%',
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    },
    flex: {
        display: 'flex'
    }
}))

function LocationReports(props) {
    const classes = useStyles()
    const fromShakeableRef = useRef(null)
    const locationId = props.match.params.locationId
    const isNew = locationId === 'new'
    const urlLocation = useLocation()

    const { isLoading, locale } = useSelector(state => state.main)
    const { locationReports, location, isLoadingMoreReports } = useSelector(
        state => state.locations
    )
    const [isExporting, setIsExporting] = useState(false)

    const { filters, d, isLoadingFilters } = useReportsFilters(
        urlToSearchState(urlLocation),
        UserPermissions.isAllOriginalGranted(),
        null,
        isNew ? null : locationId
    )
    useSyncUrlState(filters)

    const dispatch = useDispatch()

    const loadMoreReports = useCallback(
        debounce(() => {
            if (!isLoadingFilters) {
                dispatch(
                    actions.locationMoreReportsRequest(locationId, filters)
                )
            }
        }, 300)
    )

    useEffect(() => {
        if (!isNew && !isLoadingFilters)
            dispatch(actions.locationReportsRequest(locationId, filters))
    }, [locationId, filters, isLoadingFilters])

    useEffect(() => {
        return () => {
            dispatch(actions.locationReportsSuccess(null))
        }
    }, [])

    const exportReports = async fileType => {
        if (isFiltersTimeRangeExceededForExport(filters)) {
            fromShakeableRef.current.shake()
            dispatch(
                enqueueSnackbar({
                    message: locale.reports.exportDenyMsg,
                    options: {
                        variant: 'warning'
                    }
                })
            )
            return
        }
        setIsExporting(true)
        const locationName = location.name
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = {
            fileType,
            locale_key,
            locationId,
            locationName,
            ...filters
        }
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('reports', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDeviceReportsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    return (
        <>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div
                className={classNames(classes.wrapper, {
                    [`${classes.disabledWrapper}`]: isExporting
                })}>
                <PageTitle title={locale.locations.reportsOnLocation} />
                <div className={classes.filtersWrapper}>
                    <div className={classes.exportBtnsWrapper}>
                        <ExportMenu>
                            {({ handleClose }) => (
                                <>
                                    <ExportMenu.Csv
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Excel
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Pdf
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                </>
                            )}
                        </ExportMenu>
                    </div>
                    {((UserPermissions.isAllOriginalGranted() &&
                        UserPermissions.isOriginalEntity(
                            location?.customer_id
                        )) ||
                        UserPermissions.isTraplinkedAdmin()) && (
                        <OptionSwitcher
                            className={classes.switcher}
                            options={{
                                [REPRESENTATION.all]: locale.devices.allDevices,
                                [REPRESENTATION.assigned]:
                                    locale.devices.assigned
                            }}
                            selected={
                                filters.is_assigned
                                    ? REPRESENTATION.assigned
                                    : REPRESENTATION.all
                            }
                            onChange={value =>
                                d({
                                    type: 'is_assigned',
                                    value: value === REPRESENTATION.assigned
                                })
                            }
                        />
                    )}
                    <ReportsFilters
                        onChange={d}
                        filters={filters}
                        className={classes.filters}
                        fromShakeableRef={fromShakeableRef}
                    />
                </div>
                {isLoading &&
                (!locationReports || locationReports.length === 0) ? (
                    <Loading />
                ) : null}

                {!isLoading &&
                    (!locationReports || locationReports.length === 0) && (
                        <div>{locale.reports.noReportsForRange}</div>
                    )}

                {!isEmpty(locationReports) && (
                    <ReportsTable
                        isLoading={isLoading}
                        sorting={filters.sort_by}
                        onSort={sort_by => d({ type: 'sort', sort_by })}
                        contextPage="locations"
                        reports={locationReports}
                        isLoadingMore={isLoadingMoreReports}
                        onLoadMore={loadMoreReports}
                    />
                )}
            </div>
        </>
    )
}

export default withRouter(LocationReports)
